import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { MdOutlineHome, MdOutlineRequestQuote, MdOutlineLocalShipping, MdOutlineChatBubbleOutline, MdOutlineStorefront, MdOutlineBusiness, MdOutlineLogout } from "react-icons/md";
import BrandLogo from "../../components/BrandLogo/BrandLogo";
import useViewport from "../../hooks/useViewport";
import * as supplierService from "../../services/supplierService";
import SupplierBell from "./SupplierBell";
import { useWebSocket } from "../../hooks/useWebSocket";
import "./SupplierLayout.css";

// The shell for a supplier (role 8): its own menu, bell and sign-out. A supplier works for several buyers,
// so this shell never loads anything about "a vendor"; everything comes from /proc/supplier/*.
export const SUPPLIER_NAV = [
  { key: "home", to: "/supplier", label: "Home", icon: MdOutlineHome, end: true },
  { key: "rfqs", to: "/supplier/rfqs", label: "RFQs", icon: MdOutlineRequestQuote },
  { key: "orders", to: "/supplier/orders", label: "Orders", icon: MdOutlineLocalShipping },
  { key: "messages", to: "/supplier/messages", label: "Messages", icon: MdOutlineChatBubbleOutline },
  { key: "buyers", to: "/supplier/buyers", label: "Buyers", icon: MdOutlineStorefront },
  { key: "company", to: "/supplier/company", label: "Company", icon: MdOutlineBusiness },
];

export default function SupplierLayout() {
  const navigate = useNavigate();
  const { isPhone, shell } = useViewport();
  const [me, setMe] = useState(null);
  const [unread, setUnread] = useState(0);

  // the Messages entry shows how many messages are waiting; refreshed every 30 s and at once when one arrives
  const refreshUnread = useCallback(() => {
    supplierService
      .getUnreadMessages()
      .then((res) => setUnread((res && res.data && res.data.count) || 0))
      .catch(() => {});
  }, []);
  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, 30000);
    return () => clearInterval(t);
  }, [refreshUnread]);
  useWebSocket({ onEvent: (e) => e && (e.type === "proc_message" || e.type === "proc_notification") && refreshUnread() });

  useEffect(() => {
    let alive = true;
    supplierService
      .getMe()
      .then((res) => alive && setMe(res?.data || null))
      .catch(() => alive && setMe(null));
    return () => {
      alive = false;
    };
  }, []);

  const signOut = () => {
    localStorage.clear();
    navigate("/login");
  };

  const link = (item) => (
    <NavLink key={item.key} to={item.to} end={item.end} className={({ isActive }) => (isActive ? "active" : undefined)}>
      <item.icon size={isPhone ? 22 : 20} />
      <span>{item.label}</span>
      {item.key === "messages" && unread > 0 ? <span className="supplier-badge" aria-label={`${unread} unread`}>{unread > 99 ? "99+" : unread}</span> : null}
    </NavLink>
  );

  return (
    <div className="supplier-shell" data-shell={shell}>
      <header className="supplier-bar">
        <BrandLogo height={30} />
        <span className="supplier-tag">Supplier portal</span>
        <span className="supplier-spacer" />
        {me && !isPhone ? (
          <span style={{ fontSize: 13, color: "#374151", textAlign: "right", lineHeight: 1.2 }}>
            <strong>{me.user.first_name}</strong>
            {me.company ? <div style={{ fontSize: 12, color: "#6B7280" }}>{me.company.name}</div> : null}
          </span>
        ) : null}
        <SupplierBell />
        <button type="button" className="btn p-1" aria-label="Sign out" title="Sign out" onClick={signOut} style={{ background: "none", border: "none", color: "#4B5563" }}>
          <MdOutlineLogout size={24} />
        </button>
      </header>
      {isPhone ? null : <nav className="supplier-side" aria-label="Supplier menu">{SUPPLIER_NAV.map(link)}</nav>}
      <main className="supplier-main">
        <Outlet context={{ me }} />
      </main>
      {isPhone ? <nav className="supplier-bottom" aria-label="Supplier menu">{SUPPLIER_NAV.map(link)}</nav> : null}
    </div>
  );
}
